import isUndefined from 'lodash-es/isUndefined';
import { SchedulerAction, SchedulerLike, Subscription } from 'rxjs';
import { isKarmaTestEnv } from '../env';

/**
 * @file
 * Implements an RxJS scheduler for scheduling work in animation frames.
 *
 * Unlike the stock scheduler, this scheduler can target a specific window (such as a child window) and it
 * schedules everything in the next animation frame regardless of the provided delay.
 */

/** Subset of the `Window` interface used by the animation frame scheduler. */
export interface AnimationFrameSchedulerDelegate extends AnimationFrameProvider {
  performance: {
    now(): number;
  };
}

const sharedSchedulers = new WeakMap<AnimationFrameSchedulerDelegate, SchedulerLike>();

/** Returns a shared animation frame scheduler for the given window. */
export function getAnimationFrameScheduler(window: AnimationFrameSchedulerDelegate): SchedulerLike {
  const existingScheduler = sharedSchedulers.get(window);
  const scheduler = existingScheduler || new AnimationFrameScheduler(window);

  if (scheduler !== existingScheduler) {
    sharedSchedulers.set(window, scheduler);
  }

  return scheduler;
}

/** Creates a new animation frame scheduler for the given window. For testing purposes. */
export function createAnimationFrameScheduler(window: AnimationFrameSchedulerDelegate): SchedulerLike {
  return new AnimationFrameScheduler(window);
}

class AnimationFrameScheduler implements SchedulerLike {
  private readonly delegate: AnimationFrameSchedulerDelegate;
  private handle: number | undefined;
  private actions: Array<AnimationFrameSchedulerAction<any>>;

  constructor(delegate: AnimationFrameSchedulerDelegate) {
    this.delegate = delegate;
    this.handle = undefined;
    this.actions = [];
  }

  now() {
    // This matches the timestamp passed into animation frame callbacks.
    return this.delegate.performance.now();
  }

  schedule<T>(work: AnimationFrameSchedulerWork<T>, _delay: unknown, state?: T): Subscription {
    return new AnimationFrameSchedulerAction(this, work).schedule(state);
  }

  scheduleAction(action: AnimationFrameSchedulerAction<any>) {
    // Enqueue the action.
    this.actions.push(action);

    // Request an animation frame, if needed.
    if (isUndefined(this.handle)) {
      this.handle = this.delegate.requestAnimationFrame(() => this.flushActions());
    }
  }

  unscheduleAction(action: AnimationFrameSchedulerAction<any>) {
    // Remove all instances of the action from the queue.
    let index = this.actions.indexOf(action);
    while (index !== -1) {
      this.actions.splice(index, 1);
      index = this.actions.indexOf(action, index);
    }

    // Cancel the animation frame, if needed.
    if (this.actions.length === 0 && !isUndefined(this.handle)) {
      this.delegate.cancelAnimationFrame(this.handle);
      this.handle = undefined;
    }
  }

  private flushActions() {
    // Dequeue all actions first so that recursively scheduled actions are buffered towards a new animation frame.
    const { actions } = this;
    this.handle = undefined;
    this.actions = [];

    let index = 0;
    try {
      // Execute the actions.
      for (; index < actions.length; index++) {
        actions[index].execute();
      }
    } finally {
      // If an action threw, close out any remaining actions.
      for (; index < actions.length; index++) {
        actions[index].unsubscribe();
      }
    }
  }
}

class AnimationFrameSchedulerAction<T> extends Subscription implements SchedulerAction<T> {
  private readonly scheduler: AnimationFrameScheduler;
  private readonly work: AnimationFrameSchedulerWork<T>;
  private states: Array<T | undefined>;

  constructor(scheduler: AnimationFrameScheduler, work: AnimationFrameSchedulerWork<T>) {
    super();

    this.scheduler = scheduler;
    this.work = work;
    this.states = [];
  }

  schedule(state?: T): Subscription {
    if (this.closed) {
      return this;
    }

    // Enqueue the state.
    this.states.push(state);
    this.scheduler.scheduleAction(this);
    return this;
  }

  execute() {
    if (this.closed) {
      return;
    }

    if (this.states.length === 0) {
      if (isKarmaTestEnv) {
        throw new Error('Unexpected attempt to execute an action without pending states');
      }
      return;
    }

    // Dequeue a state.
    const state = this.states.shift();

    // If the action has been rescheduled after this state, don't execute it.
    if (this.states.length > 0) {
      return;
    }

    this.work(state);
  }

  unsubscribe() {
    this.states.length = 0;
    this.scheduler.unscheduleAction(this);

    super.unsubscribe();
  }
}

type AnimationFrameSchedulerWork<T> = (this: SchedulerAction<T>, state?: T) => void;
